/* @jsx h */
import { defineComponent, h, ref, onMounted, type Ref } from "vue";
import type { RestaurantProfileData, CountryOption, CurrencyOption, TimezoneOption } from "../types";
import { loadCountries, loadTimezones, loadCurrencies, getLocales } from "../helpers/countryData";

export const RegionalSettingsSection = defineComponent<{
  data: Partial<RestaurantProfileData>;
  onUpdate: <K extends keyof RestaurantProfileData>(field: K, value: RestaurantProfileData[K]) => void;
}>({
  name: "RegionalSettingsSection",
  props: ["data", "onUpdate"],
  setup(props) {
    const countries: Ref<CountryOption[]> = ref([]);
    const loadingCountries = ref(true);
    const currencies: Ref<CurrencyOption[]> = ref([]);
    const loadingCurrencies = ref(true);
    const timezones: Ref<TimezoneOption[]> = ref([]);
    const loadingTimezones = ref(true);
    const locales = getLocales();

    onMounted(async () => {
      countries.value = await loadCountries();
      loadingCountries.value = false;
      timezones.value = await loadTimezones();
      loadingTimezones.value = false;
      currencies.value = await loadCurrencies();
      loadingCurrencies.value = false;
    });

    return () => (
      <div class="bg-base-100 l-box-shadow  rounded-xl p-6 shadow-sm border ">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-success text-xl">language</span>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-accent">Regional Settings</h3>
            <p class="text-sm text-base-content">Localization and format preferences</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">Country/Jurisdiction *</label>
            <select
              value={props.data.jurisdiction}
              onChange={(e) => props.onUpdate("jurisdiction", (e.target as HTMLSelectElement).value)}
              class="select select-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              disabled={loadingCountries.value}
              required
            >
              {loadingCountries.value ? (
                <option>Loading countries...</option>
              ) : (
                <>
                  <option value="">Select country</option>
                  {countries.value.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.flag ? `${country.flag} ` : ""}
                      {country.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">Timezone *</label>
            <select
              value={props.data.timezone}
              onChange={(e) => props.onUpdate("timezone", (e.target as HTMLSelectElement).value)}
              class="select select-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              disabled={loadingTimezones.value}
              required
            >
              {loadingTimezones.value ? (
                <option>Loading timezones...</option>
              ) : (
                <>
                  <option value="">Select timezone</option>
                  {timezones.value.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.offset})
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">Currency *</label>
            <select
              value={props.data.currency}
              onChange={(e) => props.onUpdate("currency", (e.target as HTMLSelectElement).value)}
              class="select select-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              disabled={loadingCurrencies.value}
              required
            >
              {loadingCurrencies.value ? (
                <option>Loading currencies...</option>
              ) : (
                <>
                  <option value="">Select currency</option>
                  {currencies.value.map((curr) => (
                    <option key={curr.value} value={curr.value}>
                      {curr.symbol} - {curr.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">Language/Locale *</label>
            <select
              value={props.data.locale}
              onChange={(e) => props.onUpdate("locale", (e.target as HTMLSelectElement).value)}
              class="select select-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              required
            >
              <option value="">Select locale</option>
              {locales.map((locale) => (
                <option key={locale.value} value={locale.value}>
                  {locale.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  },
});
